/**
 * Plain-language explanations for every money figure on the invoice screens.
 * Shown by <InfoTip>. Keep these in step with BE invoices/dealAmounts.ts
 * (due buckets) and utils/invoiceDealAmounts.js (deal reconcile).
 */

// ---- Summary cards (Invoices list, Management dashboard) -------------------

export const DUE_BUCKET_HELP = {
  due: {
    title: 'Due',
    body: 'Invoices already raised and not yet paid, with a due date in this month or later. Not late yet.',
    formula: 'Unpaid invoices due this month or later − advance credit left after clearing Overdue',
    example: 'An instalment of ₹5,00,000 due on 25th of this month, unpaid → counts here.'
  },
  overdue: {
    title: 'Overdue',
    body: 'Invoices already raised and not paid, whose due month has passed. Chase these first. Advance credit is used against Overdue before anything else.',
    formula: 'Unpaid invoices due in an earlier month − buyer’s advance credit',
    example: 'Invoice due 10 Aug, unpaid in October → Overdue.'
  },
  pending: {
    title: 'Total Pending',
    body: 'Everything invoiced and still unpaid. Late fees are shown separately and are not included.',
    formula: 'Total Pending = Overdue + Due'
  },
  uninvoiced: {
    title: 'Uninvoiced',
    body: 'Sale value that has not been invoiced yet — usually because the construction stage is not reached or not unlocked. The buyer does not owe this yet.',
    formula:
      'Sale price + debit notes + extra charges − credit notes − all invoices raised − advance credit left after Overdue and Due',
    example: 'Sale ₹80L, invoices raised ₹47L, no credit → Uninvoiced ₹33L.'
  },
  late_fee: {
    title: 'Late Fee',
    body: 'Late-fee invoices charged on delayed payments and not yet paid. Kept out of Due / Overdue so the instalment figures stay clean.',
    formula: 'Sum of unpaid late-fee invoices'
  },
  sale_price: {
    title: 'Sale price',
    body: 'Closed deal value agreed with the buyer (before debit / credit notes).'
  },
  never_invoiced: {
    title: 'Never invoiced',
    body: 'Approved deal with no invoice raised at all. Usually a stage needs unlocking or invoice generation failed — open the deal and check Stage unlock.'
  }
};

export const DUE_BUCKET_SCOPE_NOTE =
  'Approved deals only. Cancelled invoices are ignored. Months are counted in IST.';

// ---- Invoice Details: deal summary ------------------------------------------

export const DEAL_HELP = {
  totalPrice: {
    title: 'Total Price',
    body: 'Closed sale value of this unit as approved on the deal.'
  },
  cashAllocated: {
    title: 'Cash allocated to invoices',
    body: 'Part of the money received that has been applied against invoices (marked paid / part-paid).',
    formula: 'Sum of payments applied to invoice lines'
  },
  openInvoiceBalance: {
    title: 'Open invoice balance',
    body: 'Everything the buyer still has to pay on this deal, before using any advance credit. Includes invoices not yet raised.',
    formula: 'Unpaid invoices + uninvoiced sale value',
    example: 'Unpaid ₹2L + not yet invoiced ₹10L → ₹12L.'
  },
  verifiedCredit: {
    title: 'Verified cash credit (advance)',
    body: 'Money the buyer paid that is not applied to any invoice yet — an advance or excess payment held on the deal, backed by a recorded receipt. Use "Apply verified credit" to settle open invoices with it, or refund / transfer it.'
  },
  legacyCredit: {
    title: 'Legacy credit (unverified)',
    body: 'Old credit balance carried over from before the payment ledger existed. Not counted in any amount until finance checks the receipt and clicks "Verify legacy credit".'
  },
  netToCollect: {
    title: 'Net amount to collect',
    body: 'What the buyer still owes after using their advance credit. This is the figure to match with Tally.',
    formula: 'Open invoice balance − verified cash credit (never below 0)',
    example: 'Open ₹12L, verified credit ₹1L → ₹11L to collect.'
  },
  commercialCredit: {
    title: 'Commercial credit applied',
    body: 'Credit notes raised on this deal (discounts, price corrections, write-offs). They reduce what is owed but are not cash — nothing to refund.',
    formula: 'Sum of active credit notes'
  },
  tallyLedger: {
    title: 'Tally ledger',
    body: 'Buyer’s ledger balance entered from Tally. Should equal Net amount to collect; any difference needs checking.'
  },
  lateFeesDue: {
    title: 'Late fees due',
    body: 'Unpaid late-fee invoices on this deal. Not part of Net amount to collect.'
  },
  creditDebitNotes: {
    title: 'Debit / Credit notes',
    body: 'Debit note: adds an amount the buyer owes (e.g. extra work, charges) — increases the sale value. Credit note: reduces what the buyer owes (discount, price correction, write-off) — no cash moves. Cancelled notes are ignored.'
  }
};

// ---- Invoice Details: Accounts reconciliation -------------------------------

export const RECONCILE_HELP = {
  panel: {
    title: 'Accounts reconciliation',
    body: 'Checks that every rupee received is accounted for, and that Housingram agrees with Tally.',
    formula: 'Received = Applied to invoices + Held as credit + Refunds out + Unexplained'
  },
  received: {
    title: 'Received',
    body: 'Total of all payments recorded on this deal (all modes: cheque, NEFT, cash…).'
  },
  applied: {
    title: 'Applied to invoices',
    body: 'Portion of the payments used to pay invoices.'
  },
  held: {
    title: 'Held as credit',
    body: 'Portion kept as the buyer’s advance (verified cash credit). It will be used against future or open invoices.'
  },
  unexplained: {
    title: 'Unexplained',
    body: 'Money received that is neither applied to an invoice nor held as credit. Should be ₹0 — if not, open the payment and apply it or park it as credit.',
    formula: 'Received − Applied − Held as credit − refunds / transfers out'
  },
  housingramNet: {
    title: 'Housingram: net amount to collect',
    body: 'Same as "Net amount to collect" above — what the buyer still owes after advance credit.'
  },
  tallyBalance: {
    title: 'Tally ledger balance',
    body: 'Enter the buyer’s closing balance from their Tally ledger (amount receivable).'
  },
  result: {
    title: 'Result',
    body: 'Matches when Tally balance equals Housingram net amount to collect (to the rupee). "Differs by" shows the gap to investigate — usually a payment or credit note missing on one side.',
    formula: 'Tally balance − Housingram net amount to collect'
  }
};
